import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Icon } from '../Icon';
import { funnelMilestone } from '../../api/funnel';
import {
  cancelScheduledGame,
  createScheduledGame,
  listMyScheduledGames,
  type ScheduledGame,
} from '../../api/scheduledGames';
import {
  defaultScheduleEpoch,
  epochToLocalInput,
  formatCountdown,
  formatGameTime,
  googleCalendarUrl,
  isValidEmail,
  localInputToEpoch,
} from '../../lib/scheduledGameTime';

/**
 * §6.E "Schedule a game with friends" — the host's scheduling surface.
 *
 * <p>Three views in one portal:
 * <ul>
 *   <li><b>form</b> — pick a time, optional title, optional reel-skin theme,
 *       and add friends' emails as chips (validated, capped, zero allowed —
 *       link-only is fine). "Create" → {@link createScheduledGame}.
 *   <li><b>success</b> — the tasteful shareable-link moment: copy + native
 *       share of the link, add-to-calendar (.ics + Google), and the invited
 *       emails. Fires {@code funnelMilestone('game_scheduled')}.
 *   <li><b>upcoming</b> — the host's existing scheduled games with re-share +
 *       cancel actions ({@link listMyScheduledGames} / {@link cancelScheduledGame}).
 * </ul>
 *
 * <p>Visual language mirrors {@code JoinByCodeModal} — a radial violet glow
 * over a deep-purple card, amber accents, "Fredoka". Portals to the body so it
 * floats above the canvas + HUD.
 */
export interface ScheduleGameModalProps {
  /** The host's stable player id. Null in a cold/preview state disables
   *  creating (the form still renders, the CTA explains it's loading). */
  playerId?: string | null;
  /** The host's currently-selected theme id — the default reel skin when they
   *  don't pick one. */
  currentThemeId?: string | null;
  /** Themes the host can pick from (their unlocked reel skins). Optional; an
   *  empty list just hides the picker (the server defaults the theme). */
  availableThemes?: readonly { id: string; name: string; iconUrl?: string }[];
  onClose: () => void;
}

const MAX_INVITEES = 10;

export function ScheduleGameModal({
  playerId,
  currentThemeId = null,
  availableThemes = [],
  onClose,
}: ScheduleGameModalProps) {
  const [view, setView] = useState<'form' | 'success' | 'upcoming'>('form');

  // ----- form state -----
  const [when, setWhen] = useState<string>(() =>
    epochToLocalInput(defaultScheduleEpoch()),
  );
  const [title, setTitle] = useState<string>('');
  const [themeId, setThemeId] = useState<string | null>(currentThemeId);
  const [emails, setEmails] = useState<string[]>([]);
  const [emailDraft, setEmailDraft] = useState<string>('');
  const [emailError, setEmailError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  // ----- success state -----
  const [created, setCreated] = useState<ScheduledGame | null>(null);
  const [flash, setFlash] = useState<string | null>(null);

  const showFlash = (msg: string): void => {
    setFlash(msg);
    window.setTimeout(() => setFlash(null), 1600);
  };

  const epoch = useMemo(() => localInputToEpoch(when), [when]);
  const timeValid = Number.isFinite(epoch) && epoch > Date.now();

  const commitEmail = (raw: string): void => {
    const value = raw.trim().toLowerCase();
    if (!value) return;
    if (!isValidEmail(value)) {
      setEmailError('That doesn’t look like an email.');
      return;
    }
    if (emails.includes(value)) {
      setEmailError('Already added.');
      setEmailDraft('');
      return;
    }
    if (emails.length >= MAX_INVITEES) {
      setEmailError(`That’s the max (${MAX_INVITEES}). Link-share for more.`);
      return;
    }
    setEmails((prev) => [...prev, value]);
    setEmailDraft('');
    setEmailError(null);
  };

  const onEmailKey = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'Enter' || e.key === ',' || e.key === ' ') {
      e.preventDefault();
      commitEmail(emailDraft);
    } else if (e.key === 'Backspace' && !emailDraft && emails.length > 0) {
      setEmails((prev) => prev.slice(0, -1));
    }
  };

  const submit = (): void => {
    if (busy || !playerId || !timeValid) return;
    setBusy(true);
    setFormError(null);
    // A draft email still in the box but not yet chipped — fold it in.
    const pending = emailDraft.trim().toLowerCase();
    const finalEmails =
      pending && isValidEmail(pending) && !emails.includes(pending)
        ? [...emails, pending].slice(0, MAX_INVITEES)
        : emails;
    void createScheduledGame({
      hostPlayerId: playerId,
      scheduledFor: epoch,
      themeId: themeId ?? undefined,
      title: title.trim() || undefined,
      inviteeEmails: finalEmails.length > 0 ? finalEmails : undefined,
    })
      .then((game) => {
        // Playtest funnel — the host successfully scheduled a game.
        // Once-per-device, fire-and-forget; never blocks the success view.
        funnelMilestone('game_scheduled', playerId);
        setCreated(game);
        setView('success');
      })
      .catch(() => {
        setFormError('Couldn’t schedule that game. Try again in a moment.');
      })
      .finally(() => setBusy(false));
  };

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Schedule a game with friends"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'rgba(8, 4, 22, 0.74)',
        backdropFilter: 'blur(6px)',
        WebkitBackdropFilter: 'blur(6px)',
        fontFamily: '"Fredoka", system-ui',
        padding: 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          position: 'relative',
          width: '100%',
          maxWidth: 400,
          maxHeight: '88vh',
          overflowY: 'auto',
          borderRadius: 24,
          padding: '24px 22px 22px',
          color: '#fff',
          background:
            'radial-gradient(ellipse 460px 280px at 50% 0%, rgba(139,92,246,0.30) 0%, transparent 64%), linear-gradient(180deg, #1c0e36 0%, #120726 100%)',
          border: '1.5px solid rgba(255,228,138,0.35)',
          boxShadow: '0 24px 60px rgba(0,0,0,0.62)',
        }}
      >
        {flash ? <Toast text={flash} /> : null}

        {view === 'success' && created ? (
          <SuccessView
            game={created}
            onCopy={(t, label) => copyText(t, () => showFlash(label))}
            onShowUpcoming={playerId ? () => setView('upcoming') : undefined}
            onDone={onClose}
          />
        ) : view === 'upcoming' && playerId ? (
          <UpcomingView
            playerId={playerId}
            onBack={() => setView('form')}
            onCopy={(t, label) => copyText(t, () => showFlash(label))}
          />
        ) : (
          <>
            <Eyebrow>Play with friends</Eyebrow>
            <Title>Schedule a game</Title>
            <p style={subtitleStyle}>
              Pick a time, invite friends, and we’ll drop everyone into the
              same wheel when it’s time.
            </p>

            {/* When */}
            <Field label="When">
              <input
                type="datetime-local"
                value={when}
                onChange={(e) => setWhen(e.target.value)}
                style={inputStyle}
              />
              {timeValid ? (
                <Hint>{formatGameTime(epoch)} — {formatCountdown(epoch)}</Hint>
              ) : (
                <Hint tone="error">Pick a time in the future.</Hint>
              )}
            </Field>

            {/* Title (optional) */}
            <Field label="Title (optional)">
              <input
                type="text"
                value={title}
                maxLength={60}
                placeholder="Friday night spins"
                onChange={(e) => setTitle(e.target.value)}
                style={inputStyle}
              />
            </Field>

            {/* Theme (optional) */}
            {availableThemes.length > 0 ? (
              <Field label="Reel skin (optional)">
                <div
                  style={{
                    display: 'flex',
                    gap: 8,
                    overflowX: 'auto',
                    paddingBottom: 4,
                  }}
                >
                  {availableThemes.map((t) => {
                    const selected = (themeId ?? currentThemeId) === t.id;
                    return (
                      <button
                        key={t.id}
                        type="button"
                        onClick={() => setThemeId(t.id)}
                        style={{
                          flexShrink: 0,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 6,
                          padding: '7px 12px',
                          borderRadius: 999,
                          cursor: 'pointer',
                          fontFamily: 'inherit',
                          fontSize: 12,
                          fontWeight: 700,
                          color: selected ? '#1a0830' : 'rgba(255,255,255,0.8)',
                          border: selected
                            ? '1.5px solid rgba(255,228,138,0.9)'
                            : '1px solid rgba(255,255,255,0.15)',
                          background: selected
                            ? 'linear-gradient(180deg, #ffe48a 0%, #f5c451 100%)'
                            : 'rgba(255,255,255,0.05)',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {t.iconUrl ? (
                          <img
                            src={t.iconUrl}
                            alt=""
                            width={18}
                            height={18}
                            style={{ borderRadius: 5, objectFit: 'cover' }}
                          />
                        ) : null}
                        {t.name}
                      </button>
                    );
                  })}
                </div>
              </Field>
            ) : null}

            {/* Invitees */}
            <Field label={`Invite friends (optional${emails.length ? ` · ${emails.length}/${MAX_INVITEES}` : ''})`}>
              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 6,
                  padding: emails.length ? '8px 8px 4px' : 0,
                }}
              >
                {emails.map((em) => (
                  <span
                    key={em}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 6,
                      padding: '5px 8px 5px 11px',
                      borderRadius: 999,
                      background: 'rgba(139,92,246,0.28)',
                      border: '1px solid rgba(167,139,250,0.5)',
                      fontSize: 12,
                      fontWeight: 600,
                    }}
                  >
                    {em}
                    <button
                      type="button"
                      aria-label={`Remove ${em}`}
                      onClick={() =>
                        setEmails((prev) => prev.filter((x) => x !== em))
                      }
                      style={{
                        border: 'none',
                        background: 'transparent',
                        color: 'rgba(255,255,255,0.7)',
                        cursor: 'pointer',
                        fontSize: 13,
                        lineHeight: 1,
                        padding: 0,
                      }}
                    >
                      ✕
                    </button>
                  </span>
                ))}
              </div>
              <input
                type="email"
                inputMode="email"
                autoCapitalize="off"
                autoCorrect="off"
                spellCheck={false}
                value={emailDraft}
                placeholder={
                  emails.length >= MAX_INVITEES
                    ? 'Max reached — share the link for more'
                    : 'friend@email.com'
                }
                disabled={emails.length >= MAX_INVITEES}
                onChange={(e) => {
                  setEmailDraft(e.target.value);
                  if (emailError) setEmailError(null);
                }}
                onKeyDown={onEmailKey}
                onBlur={() => emailDraft && commitEmail(emailDraft)}
                style={{ ...inputStyle, marginTop: emails.length ? 6 : 0 }}
              />
              {emailError ? (
                <Hint tone="error">{emailError}</Hint>
              ) : (
                <Hint>Press enter to add. Leave empty for link-only.</Hint>
              )}
            </Field>

            {formError ? (
              <div style={{ marginTop: 4, marginBottom: 4, fontSize: 12, fontWeight: 600, color: '#ff9bb0' }}>
                {formError}
              </div>
            ) : null}

            <button
              type="button"
              onClick={submit}
              disabled={busy || !playerId || !timeValid}
              style={{
                marginTop: 16,
                width: '100%',
                padding: '14px 0',
                borderRadius: 999,
                border: '1.5px solid rgba(255,228,138,0.6)',
                background:
                  busy || !playerId || !timeValid
                    ? 'rgba(255,255,255,0.08)'
                    : 'linear-gradient(180deg, #ff6bd6 0%, #d63dc4 100%)',
                color: '#fff',
                fontFamily: 'inherit',
                fontSize: 15,
                fontWeight: 900,
                letterSpacing: 0.4,
                cursor:
                  busy || !playerId || !timeValid ? 'default' : 'pointer',
                opacity: busy || !playerId || !timeValid ? 0.6 : 1,
              }}
            >
              {busy
                ? 'Scheduling…'
                : !playerId
                  ? 'Loading…'
                  : 'Create invite'}
            </button>

            <div
              style={{
                marginTop: 12,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              {playerId ? (
                <button
                  type="button"
                  onClick={() => setView('upcoming')}
                  style={linkBtnStyle}
                >
                  My upcoming games
                </button>
              ) : (
                <span />
              )}
              <button type="button" onClick={onClose} style={linkBtnStyle}>
                Maybe later
              </button>
            </div>
          </>
        )}
      </div>
    </div>,
    document.body,
  );
}

/* ------------------------------------------------------------------ */
/* Success view — the tasteful shareable-link moment                   */
/* ------------------------------------------------------------------ */

function SuccessView({
  game,
  onCopy,
  onShowUpcoming,
  onDone,
}: {
  game: ScheduledGame;
  onCopy: (text: string, label: string) => void;
  onShowUpcoming?: () => void;
  onDone: () => void;
}) {
  const share = (): void => {
    const text = `Join my Goodtime game${game.title ? ` “${game.title}”` : ''} on ${formatGameTime(game.scheduledFor)}: ${game.shareUrl}`;
    if (navigator.share) {
      void navigator
        .share({ title: 'Goodtime', text, url: game.shareUrl })
        .catch(() => {
          /* user dismissed — no-op */
        });
    } else {
      onCopy(game.shareUrl, 'Link copied');
    }
  };

  const gcalUrl = googleCalendarUrl({
    title: game.title || 'Goodtime game',
    startMs: game.scheduledFor,
    details: `Join the table: ${game.shareUrl}`,
  });

  return (
    <div style={{ textAlign: 'center' }}>
      {/* Celebratory hero */}
      <div
        aria-hidden
        style={{
          margin: '2px auto 10px',
          width: 64,
          height: 64,
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 30,
          background:
            'radial-gradient(circle at 35% 30%, #FFE57F, #FFC40C 60%, #B07F00)',
          boxShadow:
            '0 0 0 6px rgba(255,196,12,0.14), 0 8px 24px rgba(255,180,40,0.4)',
        }}
      >
        🎉
      </div>
      <Eyebrow>You’re all set</Eyebrow>
      <Title>{game.title ? game.title : 'Game scheduled!'}</Title>
      <p style={{ ...subtitleStyle, textAlign: 'center' }}>
        {formatGameTime(game.scheduledFor)} ·{' '}
        {formatCountdown(game.scheduledFor)}
      </p>

      {/* Shareable link */}
      <button
        type="button"
        onClick={() => onCopy(game.shareUrl, 'Link copied')}
        aria-label="Copy share link"
        style={{
          marginTop: 6,
          width: '100%',
          textAlign: 'left',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 10,
          padding: '11px 14px',
          borderRadius: 14,
          border: '1px solid rgba(255,228,138,0.4)',
          background: 'rgba(0,0,0,0.3)',
          cursor: 'pointer',
          fontFamily: 'inherit',
        }}
      >
        <span style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
          <span style={{ fontSize: 10, letterSpacing: 0.5, color: 'rgba(255,255,255,0.5)' }}>
            SHAREABLE LINK
          </span>
          <span
            style={{
              fontSize: 13,
              fontWeight: 700,
              color: '#ffe48a',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {game.shareUrl.replace(/^https?:\/\//, '')}
          </span>
        </span>
        <span aria-hidden style={{ fontSize: 16, color: 'rgba(252,211,77,0.8)' }}>⧉</span>
      </button>

      {/* Share + copy */}
      <div style={{ display: 'flex', gap: 10, marginTop: 12 }}>
        <button
          type="button"
          onClick={share}
          style={{
            flex: 1.4,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 8,
            padding: '12px 0',
            borderRadius: 999,
            border: '1.5px solid rgba(255,228,138,0.6)',
            background: 'linear-gradient(180deg, #ffe48a 0%, #f5c451 100%)',
            color: '#3a1d00',
            fontFamily: 'inherit',
            fontSize: 14,
            fontWeight: 900,
            cursor: 'pointer',
          }}
        >
          <Icon name="share" size={15} alt="" />
          Share
        </button>
        <button
          type="button"
          onClick={() => onCopy(game.shareUrl, 'Link copied')}
          style={{
            flex: 1,
            padding: '12px 0',
            borderRadius: 999,
            border: '1px solid rgba(255,255,255,0.2)',
            background: 'rgba(255,255,255,0.05)',
            color: 'rgba(255,255,255,0.88)',
            fontFamily: 'inherit',
            fontSize: 13,
            fontWeight: 700,
            cursor: 'pointer',
          }}
        >
          Copy
        </button>
      </div>

      {/* Add to calendar */}
      <div style={{ display: 'flex', gap: 10, marginTop: 10 }}>
        <a
          href={game.icsUrl}
          target="_blank"
          rel="noopener noreferrer"
          style={calBtnStyle}
        >
          <Icon name="clock" size={14} alt="" />
          Add to calendar
        </a>
        <a
          href={gcalUrl}
          target="_blank"
          rel="noopener noreferrer"
          style={calBtnStyle}
        >
          Google Calendar
        </a>
      </div>

      {/* Invited emails */}
      {game.inviteeCount > 0 ? (
        <div
          style={{
            marginTop: 14,
            padding: '10px 12px',
            borderRadius: 12,
            background: 'rgba(255,255,255,0.04)',
            border: '1px solid rgba(255,255,255,0.08)',
            textAlign: 'left',
          }}
        >
          <div style={{ fontSize: 11, letterSpacing: 0.5, color: 'rgba(255,255,255,0.5)' }}>
            INVITED ({game.inviteeCount})
          </div>
          <div style={{ marginTop: 4, fontSize: 12, color: 'rgba(255,255,255,0.8)' }}>
            {game.emailEnabled
              ? 'We’ll email each friend their invite.'
              : 'Send them the link above to join your table.'}
          </div>
        </div>
      ) : (
        <div style={{ marginTop: 14, fontSize: 12, color: 'rgba(255,255,255,0.55)' }}>
          No emails added — just share the link above.
        </div>
      )}

      <div
        style={{
          marginTop: 16,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        {onShowUpcoming ? (
          <button type="button" onClick={onShowUpcoming} style={linkBtnStyle}>
            My upcoming games
          </button>
        ) : (
          <span />
        )}
        <button type="button" onClick={onDone} style={linkBtnStyle}>
          Done
        </button>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Upcoming view — the host's scheduled games                          */
/* ------------------------------------------------------------------ */

function UpcomingView({
  playerId,
  onBack,
  onCopy,
}: {
  playerId: string;
  onBack: () => void;
  onCopy: (text: string, label: string) => void;
}) {
  const [games, setGames] = useState<ScheduledGame[] | null>(null);
  const [error, setError] = useState(false);
  const [cancelling, setCancelling] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    listMyScheduledGames(playerId)
      .then((g) => {
        if (!cancelled) setGames(g);
      })
      .catch(() => {
        if (!cancelled) setError(true);
      });
    return () => {
      cancelled = true;
    };
  }, [playerId]);

  const doCancel = (game: ScheduledGame): void => {
    if (cancelling) return;
    setCancelling(game.id);
    void cancelScheduledGame(game.id, playerId)
      .then(() => {
        setGames((prev) => (prev ? prev.filter((g) => g.id !== game.id) : prev));
      })
      .catch(() => {
        /* keep the row; the player can retry */
      })
      .finally(() => setCancelling(null));
  };

  const share = (game: ScheduledGame): void => {
    const text = `Join my Goodtime game on ${formatGameTime(game.scheduledFor)}: ${game.shareUrl}`;
    if (navigator.share) {
      void navigator
        .share({ title: 'Goodtime', text, url: game.shareUrl })
        .catch(() => {
          /* dismissed */
        });
    } else {
      onCopy(game.shareUrl, 'Link copied');
    }
  };

  return (
    <div>
      <button
        type="button"
        onClick={onBack}
        style={{ ...linkBtnStyle, display: 'inline-flex', alignItems: 'center', gap: 4, marginBottom: 4 }}
      >
        <Icon name="back" size={13} alt="" /> Schedule another
      </button>
      <Title>Upcoming games</Title>

      <div style={{ marginTop: 8 }}>
        {error ? (
          <div style={emptyStyle}>Couldn’t load your games. Try again in a bit.</div>
        ) : games === null ? (
          <div style={emptyStyle}>Loading…</div>
        ) : games.length === 0 ? (
          <div style={emptyStyle}>
            No games scheduled yet. Pick a time and invite friends.
          </div>
        ) : (
          <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 10 }}>
            {games.map((game) => (
              <li
                key={game.id}
                style={{
                  padding: '12px 14px',
                  borderRadius: 14,
                  background: 'rgba(255,255,255,0.04)',
                  border: '1px solid rgba(255,255,255,0.09)',
                }}
              >
                <div style={{ fontSize: 14, fontWeight: 800, color: '#fff' }}>
                  {game.title || 'Goodtime game'}
                </div>
                <div style={{ marginTop: 2, fontSize: 12, color: '#ffe48a', fontWeight: 600 }}>
                  {formatGameTime(game.scheduledFor)} · {formatCountdown(game.scheduledFor)}
                </div>
                <div style={{ marginTop: 2, fontSize: 11, color: 'rgba(255,255,255,0.55)' }}>
                  {game.inviteeCount > 0
                    ? `${game.inviteeCount} invited`
                    : 'Link-only'}
                  {game.joinableNow ? ' · live now' : ''}
                </div>
                <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                  <button
                    type="button"
                    onClick={() => share(game)}
                    style={{
                      flex: 1,
                      padding: '8px 0',
                      borderRadius: 999,
                      border: '1px solid rgba(255,228,138,0.5)',
                      background: 'rgba(255,228,138,0.12)',
                      color: '#ffe48a',
                      fontFamily: 'inherit',
                      fontSize: 12,
                      fontWeight: 800,
                      cursor: 'pointer',
                    }}
                  >
                    Re-share
                  </button>
                  <button
                    type="button"
                    onClick={() => doCancel(game)}
                    disabled={cancelling === game.id}
                    style={{
                      flex: 1,
                      padding: '8px 0',
                      borderRadius: 999,
                      border: '1px solid rgba(255,107,138,0.4)',
                      background: 'rgba(255,107,138,0.1)',
                      color: '#ff9bb0',
                      fontFamily: 'inherit',
                      fontSize: 12,
                      fontWeight: 700,
                      cursor: cancelling === game.id ? 'default' : 'pointer',
                      opacity: cancelling === game.id ? 0.6 : 1,
                    }}
                  >
                    {cancelling === game.id ? 'Cancelling…' : 'Cancel'}
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Small shared bits                                                   */
/* ------------------------------------------------------------------ */

/** Copy text to the clipboard, then run {@code done}. Best-effort. */
function copyText(text: string, done: () => void): void {
  if (navigator.clipboard?.writeText) {
    void navigator.clipboard.writeText(text).then(done, done);
  } else {
    done();
  }
}

function Eyebrow({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: 3,
        textTransform: 'uppercase',
        color: 'rgba(252,228,240,0.75)',
        textAlign: 'center',
      }}
    >
      {children}
    </div>
  );
}

function Title({ children }: { children: React.ReactNode }) {
  return (
    <h2
      style={{
        margin: '6px 0 4px',
        fontSize: 26,
        fontWeight: 900,
        color: '#ffe48a',
        textAlign: 'center',
        textShadow: '0 2px 14px rgba(255,200,100,0.4)',
      }}
    >
      {children}
    </h2>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ marginTop: 14 }}>
      <div
        style={{
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: 0.4,
          color: 'rgba(255,255,255,0.6)',
          marginBottom: 6,
        }}
      >
        {label}
      </div>
      {children}
    </div>
  );
}

function Hint({ children, tone }: { children: React.ReactNode; tone?: 'error' }) {
  return (
    <div
      style={{
        marginTop: 6,
        fontSize: 11,
        fontWeight: 500,
        color: tone === 'error' ? '#ff9bb0' : 'rgba(255,255,255,0.5)',
      }}
    >
      {children}
    </div>
  );
}

function Toast({ text }: { text: string }) {
  return (
    <div
      style={{
        position: 'absolute',
        top: 10,
        left: '50%',
        transform: 'translateX(-50%)',
        background: 'rgba(0,0,0,0.85)',
        color: '#FCE4A0',
        fontSize: 12,
        fontWeight: 700,
        padding: '6px 14px',
        borderRadius: 999,
        border: '1px solid rgba(252,211,77,0.4)',
        boxShadow: '0 4px 14px rgba(0,0,0,0.5)',
        pointerEvents: 'none',
        whiteSpace: 'nowrap',
        zIndex: 2,
      }}
    >
      {text}
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  boxSizing: 'border-box',
  padding: '12px 14px',
  borderRadius: 12,
  border: '1.5px solid rgba(255,228,138,0.32)',
  background: 'rgba(0,0,0,0.32)',
  color: '#fff',
  fontFamily: 'inherit',
  fontSize: 15,
  fontWeight: 600,
  outline: 'none',
};

const subtitleStyle: React.CSSProperties = {
  margin: '2px 0 4px',
  fontSize: 13,
  lineHeight: 1.45,
  color: 'rgba(255,255,255,0.66)',
  fontWeight: 500,
  textAlign: 'center',
};

const linkBtnStyle: React.CSSProperties = {
  border: 'none',
  background: 'transparent',
  color: 'rgba(255,255,255,0.55)',
  fontFamily: 'inherit',
  fontSize: 12,
  fontWeight: 700,
  cursor: 'pointer',
  padding: 0,
};

const calBtnStyle: React.CSSProperties = {
  flex: 1,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 6,
  padding: '10px 0',
  borderRadius: 999,
  border: '1px solid rgba(255,255,255,0.15)',
  background: 'rgba(255,255,255,0.05)',
  color: 'rgba(255,255,255,0.85)',
  fontFamily: '"Fredoka", system-ui',
  fontSize: 12,
  fontWeight: 700,
  textDecoration: 'none',
  cursor: 'pointer',
};

const emptyStyle: React.CSSProperties = {
  padding: '24px 8px',
  textAlign: 'center',
  fontSize: 13,
  lineHeight: 1.5,
  color: 'rgba(255,255,255,0.6)',
};
